const MP_NIP_NOTE_PREFIX = 'NIP do faktury:';

class VatNumberField extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.input = this.querySelector('[data-vat-number-input]');
    this.status = this.querySelector('[data-vat-number-status]');
    if (!this.input) return;

    this.form = this.input.form || document.getElementById(this.input.getAttribute('form'));
    this.timer = null;
    this.savingPromise = null;
    this.resubmitting = false;

    const initialValue = this.normalize(this.input.value);
    this.input.value = initialValue;
    this.lastSavedValue = initialValue;
    this.updateVisualState(initialValue, { strict: false, saved: Boolean(initialValue) });

    this.onInput = () => {
      const normalized = this.normalize(this.input.value);
      if (this.input.value !== normalized) this.input.value = normalized;

      clearTimeout(this.timer);
      const result = this.updateVisualState(normalized, { strict: false });
      if (result.valid) this.timer = setTimeout(() => this.save(), 500);
    };

    this.onBlur = () => {
      clearTimeout(this.timer);
      const value = this.normalize(this.input.value);
      const result = this.updateVisualState(value, { strict: true });
      if (result.valid) this.save();
    };

    this.onInvalid = (event) => {
      event.preventDefault();
      this.updateVisualState(this.normalize(this.input.value), { strict: true });
    };

    this.onSubmit = async (event) => {
      if (this.resubmitting) return;

      const value = this.normalize(this.input.value);
      const validation = this.updateVisualState(value, { strict: true });
      if (!validation.valid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.focusInvalidField();
        return;
      }

      // Zawsze synchronizuj przed przejściem do kasy. Dzięki temu NIP trafia
      // zarówno do atrybutu zamówienia, jak i do uwag widocznych w checkout.
      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter || this.form?.querySelector('[name="checkout"]');
      const saved = await this.save({ force: true });
      if (!saved || !this.form) return;

      this.resubmitting = true;
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(submitter || undefined);
      } else {
        this.form.submit();
      }
    };

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('blur', this.onBlur);
    this.input.addEventListener('invalid', this.onInvalid);
    this.form?.addEventListener('submit', this.onSubmit, true);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('blur', this.onBlur);
    this.input?.removeEventListener('invalid', this.onInvalid);
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  normalize(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 10);
  }

  isValidPolishNip(value) {
    if (!/^\d{10}$/.test(value)) return false;
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const checksum = weights.reduce((sum, weight, index) => sum + Number(value[index]) * weight, 0) % 11;
    return checksum !== 10 && checksum === Number(value[9]);
  }

  validate(value, strict = false) {
    if (!value) return { valid: true, empty: true, message: '' };
    if (value.length < 10) {
      return {
        valid: false,
        incomplete: true,
        message: strict ? 'NIP musi mieć dokładnie 10 cyfr.' : `${value.length}/10 cyfr`
      };
    }
    if (!this.isValidPolishNip(value)) {
      return { valid: false, message: 'Ten numer NIP jest nieprawidłowy. Sprawdź cyfry.' };
    }
    return { valid: true, message: 'Poprawny NIP' };
  }

  updateVisualState(value, { strict = false, saved = false } = {}) {
    const result = this.validate(value, strict);
    this.classList.toggle('is-filled', Boolean(value));
    this.classList.toggle('is-valid', result.valid && !result.empty);
    this.classList.toggle('is-invalid', !result.valid && (strict || !result.incomplete));

    if (!value) {
      this.input.removeAttribute('aria-invalid');
      this.input.setCustomValidity('');
      this.setStatus('', '');
      return result;
    }

    if (result.valid) {
      this.input.removeAttribute('aria-invalid');
      this.input.setCustomValidity('');
      const isSaved = saved || value === this.lastSavedValue;
      this.setStatus(isSaved ? 'Zapisano do zamówienia' : result.message, isSaved ? 'saved' : 'valid');
    } else if (result.incomplete && !strict) {
      this.input.removeAttribute('aria-invalid');
      this.input.setCustomValidity('');
      this.setStatus(result.message, 'progress');
    } else {
      this.input.setAttribute('aria-invalid', 'true');
      this.input.setCustomValidity(result.message);
      this.setStatus(result.message, 'error');
    }
    return result;
  }

  setStatus(text, state) {
    if (!this.status) return;
    this.status.textContent = text;
    if (state) this.status.dataset.state = state;
    else delete this.status.dataset.state;
  }

  focusInvalidField() {
    this.input.focus({ preventScroll: true });
    this.input.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  mergeNipIntoNote(note, value) {
    const lines = String(note || '')
      .split(/\r?\n/)
      .filter((line) => !line.trim().toLowerCase().startsWith(MP_NIP_NOTE_PREFIX.toLowerCase()));

    if (value) lines.push(`${MP_NIP_NOTE_PREFIX} ${value}`);
    return lines.join('\n').trim();
  }

  syncOtherFields(value) {
    document.querySelectorAll('vat-number-field').forEach((field) => {
      if (field === this || typeof field.receiveSyncedValue !== 'function') return;
      field.receiveSyncedValue(value);
    });
  }

  receiveSyncedValue(value) {
    if (!this.input) return;
    this.input.value = value;
    this.lastSavedValue = value;
    this.updateVisualState(value, { strict: true, saved: Boolean(value) });
  }

  async save({ force = false } = {}) {
    const value = this.normalize(this.input?.value);
    const validation = this.updateVisualState(value, { strict: true });
    if (!validation.valid) return false;

    if (!force && value === this.lastSavedValue) {
      this.updateVisualState(value, { strict: true, saved: Boolean(value) });
      return true;
    }
    if (this.savingPromise) return this.savingPromise;

    const updateRoute = window.Theme?.routes?.cart_update_url || '/cart/update.js';
    const cartRoute = window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js';
    this.setStatus('Zapisywanie…', 'progress');

    this.savingPromise = (async () => {
      try {
        const cartResponse = await fetch(cartRoute, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!cartResponse.ok) throw new Error(`Nie można odczytać koszyka: HTTP ${cartResponse.status}`);
        const cart = await cartResponse.json();
        const note = this.mergeNipIntoNote(cart.note, value);

        const response = await fetch(updateRoute, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            attributes: {
              'NIP do faktury': value,
              'NIP': ''
            },
            note
          })
        });
        if (!response.ok) throw new Error(`Nie można zapisać koszyka: HTTP ${response.status}`);

        this.lastSavedValue = value;
        this.updateVisualState(value, { strict: true, saved: Boolean(value) });
        this.syncOtherFields(value);
        return true;
      } catch (error) {
        console.error('Nie udało się zapisać numeru NIP w koszyku i uwagach zamówienia.', error);
        this.setStatus('Nie udało się zapisać NIP. Spróbuj ponownie.', 'error');
        return false;
      } finally {
        this.savingPromise = null;
      }
    })();

    return this.savingPromise;
  }
}

if (!customElements.get('vat-number-field')) {
  customElements.define('vat-number-field', VatNumberField);
}
